import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';
import { useAppRouter } from '../../hooks/useAppRouter';
import '../shared/Dashboard.css';
import { fetchPowerBIEmbedToken, fetchReports, searchReports } from '../../services/api';
import { useUserGroups } from '../../hooks/useUserGroups';
import { FiXCircle, FiMaximize, FiMinimize, FiShare2 } from 'react-icons/fi';

// Helper function to validate refresh frequency (minutes)
// Returns the milliseconds value if valid, null if should not refresh
// Converts minutes to milliseconds for use with setInterval
const getRefreshFrequency = (frequency: number | undefined | null): number | null => {
  if (frequency === undefined || frequency === null || frequency === 0) {
    return null;
  }
  // Ensure it's a positive number, then convert minutes to milliseconds
  return frequency > 0 ? frequency * 60 * 1000 : null;
};

const ReportViewer: React.FC = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { updateSearchParams, setSearchParams, searchParams } = useAppRouter();
  
  // Map i18n language to Power BI locale format
  const getPowerBILocale = () => {
    const lang = i18n.language || 'en';
    const localeMap: Record<string, { language: string; formatLocale: string }> = {
      'en': { language: 'en-US', formatLocale: 'en-US' },
      'de': { language: 'de-DE', formatLocale: 'de-DE' },
    };
    return localeMap[lang] || localeMap['en'];
  };
  
  const localeSettings = getPowerBILocale();

  const openTabs = useSelector((state: any) => state.tabs.openTabs);
  const activeTab = useSelector((state: any) => state.tabs.activeTab);
  const tabReportInfo = useSelector((state: any) => state.tabs.tabReportInfo);
  const embedInfo = useSelector((state: any) => state.tabs.embedInfo);
  const loadingEmbed = useSelector((state: any) => state.tabs.loadingEmbed);
  const embedError = useSelector((state: any) => state.tabs.embedError);
  const selectedDept = useSelector((state: any) => state.sidebar.selectedDept);
  const selectedSubdept = useSelector((state: any) => state.sidebar.selectedSubdept);
  const { groups: userGroups, loading: groupsLoading } = useUserGroups();

  const closingTabRefRef = useRef<string | null>(null);
  const prevUrlParamsRef = useRef<{ workspaceId: string | null, reportId: string | null }>({ workspaceId: null, reportId: null });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [embeddedReport, setEmbeddedReport] = useState<any>(null);
  const [embedReady, setEmbedReady] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const tokenRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Refs to store latest state for use in callbacks
  const embedInfoRef = useRef(embedInfo);
  const loadingEmbedRef = useRef(loadingEmbed);
  const embedErrorRef = useRef(embedError);
  const isFullscreenRef = useRef(isFullscreen);

  // Effect 1: Sync tab state with URL
  useEffect(() => {
    const urlWorkspaceId = searchParams.get('workspaceId');
    const urlReportId = searchParams.get('reportId');
    if (
      prevUrlParamsRef.current.workspaceId !== urlWorkspaceId ||
      prevUrlParamsRef.current.reportId !== urlReportId
    ) {
      prevUrlParamsRef.current = { workspaceId: urlWorkspaceId, reportId: urlReportId };

      if (urlWorkspaceId && urlReportId) {
        const tabId = `${urlWorkspaceId}-${urlReportId}`;
        if (!openTabs.includes(tabId)) {
          dispatch({ type: 'tabs/addTab', payload: tabId });
        }
        if (activeTab !== tabId) {
          dispatch({ type: 'tabs/setActiveTab', payload: tabId });
        }
      }
      // If either param is missing, do NOT re-add or set active tab
    }
  }, [searchParams, openTabs, activeTab, dispatch]);

  // Effect 2: Fetch report info if missing
  useEffect(() => {
    const urlWorkspaceId = searchParams.get('workspaceId');
    const urlReportId = searchParams.get('reportId');
    const tabId = urlWorkspaceId && urlReportId ? `${urlWorkspaceId}-${urlReportId}` : null;

    // Skip if groups are still loading
    if (groupsLoading) {
      return;
    }

    if (
      tabId &&
      (!tabReportInfo[tabId] || !tabReportInfo[tabId].reportid)
    ) {
      // If department and subdepartment are set, use the normal fetchReports
      if (selectedDept && selectedSubdept) {
        fetchReports(selectedDept, selectedSubdept, userGroups)
          .then(reports => {
            const report = reports.find((r: any) => r.workspaceid === urlWorkspaceId && r.reportid === urlReportId);
            if (report) {
              dispatch({ type: 'tabs/setTabReportInfo', payload: { ...tabReportInfo, [tabId]: report } });
              // Update URL with department/subdepartment if not already set
              const currentDept = searchParams.get('department');
              const currentSubdept = searchParams.get('sub-department');
              if (currentDept !== report.category || currentSubdept !== report.subcategory) {
                const params = new URLSearchParams(searchParams);
                params.set('department', report.category);
                params.set('sub-department', report.subcategory);
                setSearchParams(params);
              }
            }
          })
          .catch(() => {
            // Optionally handle error
          });
      } else {
        // If department/subdepartment not set, search for the report
        // Use empty search to get all accessible reports, then filter by workspaceId and reportId
        searchReports('', userGroups)
          .then(reports => {
            const report = reports.find((r: any) => r.workspaceid === urlWorkspaceId && r.reportid === urlReportId);
            if (report) {
              dispatch({ type: 'tabs/setTabReportInfo', payload: { ...tabReportInfo, [tabId]: report } });
              // Update URL with department/subdepartment from the found report
              const params = new URLSearchParams(searchParams);
              params.set('department', report.category);
              params.set('sub-department', report.subcategory);
              setSearchParams(params);
            }
          })
          .catch(() => {
            // Optionally handle error
          });
      }
    }
  }, [searchParams, tabReportInfo, selectedDept, selectedSubdept, dispatch, userGroups, groupsLoading, setSearchParams]);

  // Update refs when state changes
  useEffect(() => {
    embedInfoRef.current = embedInfo;
    loadingEmbedRef.current = loadingEmbed;
    embedErrorRef.current = embedError;
    isFullscreenRef.current = isFullscreen;
  }, [embedInfo, loadingEmbed, embedError, isFullscreen]);

  // Function to schedule proactive token refresh before expiration
  const scheduleTokenRefreshRef = useRef<((tabId: string, expiryTime: number) => void) | null>(null);

  // Function to refresh embed token for a specific tab
  const refreshEmbedToken = useCallback(async (tabId: string) => {
    const report = tabReportInfo[tabId];
    if (!report) return;

    // Use refs to get latest state values
    const currentLoadingEmbed = loadingEmbedRef.current;
    const currentEmbedError = embedErrorRef.current;
    const currentEmbedInfo = embedInfoRef.current;
    const currentEmbeddedReport = embeddedReport;

    try {
      dispatch({ type: 'tabs/setLoadingEmbed', payload: { ...currentLoadingEmbed, [tabId]: true } });
      dispatch({ type: 'tabs/setEmbedError', payload: { ...currentEmbedError, [tabId]: null } });

      const data = await fetchPowerBIEmbedToken(report.reportid, report.workspaceid, report.type);
      const token = data.accessToken || '';
      const url = Array.isArray(data.embedUrl) && data.embedUrl[0]?.embedUrl ? data.embedUrl[0].embedUrl : '';
      // Store expiry time if provided, default to 1 hour from now if not provided
      const expiryTime = data.expiry ? new Date(data.expiry).getTime() : Date.now() + (60 * 60 * 1000);
      
      dispatch({ type: 'tabs/setEmbedInfo', payload: { ...currentEmbedInfo, [tabId]: { token, url, expiry: expiryTime } } });
      dispatch({ type: 'tabs/setLoadingEmbed', payload: { ...currentLoadingEmbed, [tabId]: false } });
      
      // Update the embedded report with the new token if it exists
      if (currentEmbeddedReport && typeof currentEmbeddedReport.setAccessToken === 'function') {
        currentEmbeddedReport.setAccessToken(token);
      }
      
      // Schedule next proactive refresh
      if (scheduleTokenRefreshRef.current) {
        scheduleTokenRefreshRef.current(tabId, expiryTime);
      }
    } catch (err: any) {
      console.error('Failed to refresh embed token:', err);
      let errorMessage = 'Failed to refresh report token';
      if (err.message && err.message.includes('expired')) {
        errorMessage = err.message;
      }
      dispatch({ type: 'tabs/setEmbedError', payload: { ...embedErrorRef.current, [tabId]: errorMessage } });
      dispatch({ type: 'tabs/setLoadingEmbed', payload: { ...loadingEmbedRef.current, [tabId]: false } });
    }
  }, [tabReportInfo, embeddedReport, dispatch]);

  // Function to schedule proactive token refresh before expiration
  const scheduleTokenRefresh = useCallback((tabId: string, expiryTime: number) => {
    // Clear any existing timer
    if (tokenRefreshTimerRef.current) {
      clearTimeout(tokenRefreshTimerRef.current);
      tokenRefreshTimerRef.current = null;
    }

    // Refresh 5 minutes before expiration (or immediately if less than 5 minutes remaining)
    const now = Date.now();
    const timeUntilExpiry = expiryTime - now;
    const refreshBuffer = 5 * 60 * 1000; // 5 minutes
    const refreshTime = Math.max(timeUntilExpiry - refreshBuffer, 1000); // At least 1 second

    tokenRefreshTimerRef.current = setTimeout(async () => {
      try {
        // Refresh token
        await refreshEmbedToken(tabId);
      } catch (error) {
        console.error('Proactive token refresh failed:', error);
      }
    }, refreshTime);
  }, [refreshEmbedToken]);

  // Store the schedule function in a ref so it can be called from refreshEmbedToken
  useEffect(() => {
    scheduleTokenRefreshRef.current = scheduleTokenRefresh;
  }, [scheduleTokenRefresh]);

  // Reset embed ready state when active tab changes
  useEffect(() => {
    setEmbedReady(false);
    // Clear token refresh timer when tab changes
    if (tokenRefreshTimerRef.current) {
      clearTimeout(tokenRefreshTimerRef.current);
      tokenRefreshTimerRef.current = null;
    }
  }, [activeTab]);

  // Cleanup token refresh timer on unmount
  useEffect(() => {
    return () => {
      if (tokenRefreshTimerRef.current) {
        clearTimeout(tokenRefreshTimerRef.current);
        tokenRefreshTimerRef.current = null;
      }
    };
  }, []);

  // Fetch embed token when a new tab becomes active
  useEffect(() => {
    if (
      !activeTab ||
      !tabReportInfo[activeTab] ||
      embedInfo[activeTab] ||
      loadingEmbed[activeTab] ||
      embedError[activeTab]
    ) {
      return;
    }

    const report = tabReportInfo[activeTab];
    dispatch({ type: 'tabs/setLoadingEmbed', payload: { ...loadingEmbed, [activeTab]: true } });
    dispatch({ type: 'tabs/setEmbedError', payload: { ...embedError, [activeTab]: null } });

    fetchPowerBIEmbedToken(report.reportid, report.workspaceid, report.type)
      .then(data => {
        const token = data.accessToken || '';
        const url = Array.isArray(data.embedUrl) && data.embedUrl[0]?.embedUrl ? data.embedUrl[0].embedUrl : '';
        // Store expiry time if provided, default to 1 hour from now if not provided
        const expiryTime = data.expiry ? new Date(data.expiry).getTime() : Date.now() + (60 * 60 * 1000);
        dispatch({ type: 'tabs/setEmbedInfo', payload: { ...embedInfo, [activeTab]: { token, url, expiry: expiryTime } } });
        dispatch({ type: 'tabs/setLoadingEmbed', payload: { ...loadingEmbed, [activeTab]: false } });
        
        // Schedule proactive token refresh (5 minutes before expiration)
        if (scheduleTokenRefreshRef.current) {
          scheduleTokenRefreshRef.current(activeTab, expiryTime);
        }
      })
      .catch(err => {
        dispatch({ type: 'tabs/setEmbedError', payload: { ...embedError, [activeTab]: 'Could not load report' } });
        dispatch({ type: 'tabs/setLoadingEmbed', payload: { ...loadingEmbed, [activeTab]: false } });
      });
  }, [activeTab, tabReportInfo, embedInfo, loadingEmbed, embedError, dispatch]);


  const handleTabClick = useCallback((tabId: string) => {
    dispatch({ type: 'tabs/setActiveTab', payload: tabId });
    const report = tabReportInfo[tabId];
    if (report) {
      updateSearchParams({ workspaceId: report.workspaceid, reportId: report.reportid });
    }
  }, [dispatch, tabReportInfo, updateSearchParams]);

  const handleCloseTab = useCallback((tabId: string) => {
    const report = tabReportInfo[tabId];
    const isActive = activeTab === tabId;

    // Set closing state
    dispatch({ type: 'tabs/setIsClosingTab', payload: true });
    closingTabRefRef.current = tabId;

    // Remove URL parameters if closing active tab
    if (isActive && report) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('workspaceId');
      newParams.delete('reportId');
      setSearchParams(newParams);
    }

    // Remove tab from state
    setTimeout(() => {
      dispatch({ type: 'tabs/removeTab', payload: tabId });

      // Set new active tab if needed
      if (isActive && openTabs.length > 1) {
        const remainingTabs = openTabs.filter((t: any) => t !== tabId);
        const newActiveTab = remainingTabs[remainingTabs.length - 1];
        dispatch({ type: 'tabs/setActiveTab', payload: newActiveTab });

        const newReport = tabReportInfo[newActiveTab];
        if (newReport) {
          updateSearchParams({ workspaceId: newReport.workspaceid, reportId: newReport.reportid });
        }
      } else if (isActive) {
        dispatch({ type: 'tabs/setActiveTab', payload: null });
      }

      // Clear closing state
      dispatch({ type: 'tabs/setIsClosingTab', payload: false });
      closingTabRefRef.current = null;
    }, 0);
  }, [activeTab, openTabs, tabReportInfo, searchParams, setSearchParams, updateSearchParams, dispatch]);

  const getReportById = useCallback((tabId: any) => {
    return tabReportInfo[tabId];
  }, [tabReportInfo]);

  const handleShare = async () => {
    if (!activeTab) return;
    
    const report = tabReportInfo[activeTab];
    if (!report) return;

    // Build the URL with current report parameters
    const params = new URLSearchParams();
    params.set('workspaceId', report.workspaceid);
    params.set('reportId', report.reportid);
    if (report.category) params.set('department', report.category);
    if (report.subcategory) params.set('sub-department', report.subcategory);
    
    const reportUrl = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
    
    try {
      await navigator.clipboard.writeText(reportUrl);
      setShowShareModal(true);
      // Auto-hide modal after 3 seconds
      setTimeout(() => {
        setShowShareModal(false);
      }, 3000);
    } catch (error) {
      console.error('Failed to copy URL to clipboard:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = reportUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setShowShareModal(true);
        setTimeout(() => {
          setShowShareModal(false);
        }, 3000);
      } catch (err) {
        console.error('Fallback copy failed:', err);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleFullscreen = () => {
    const next = !isFullscreen;
    setIsFullscreen(next);
    isFullscreenRef.current = next;

    // Toggle layout-level fullscreen class on the dashboard root,
    // so that the header/sidebar are hidden and the report fills the page.
    try {
      const root = document.querySelector('.dashboard-root');
      if (root) {
        if (next) {
          root.classList.add('reports-fullscreen');
        } else {
          root.classList.remove('reports-fullscreen');
        }
      }
    } catch (error) {
      console.error('Error toggling layout fullscreen class:', error);
    }

    // When exiting fullscreen layout mode, clear any active refresh interval
    if (!next && refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  };

  // Effect: Set up refresh interval when in fullscreen mode
  useEffect(() => {
    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    // Only set up refresh if in fullscreen mode and we have an embedded report
    if (!isFullscreen || !embeddedReport || !activeTab) {
      return;
    }

    const report = tabReportInfo[activeTab];
    if (!report) {
      return;
    }

    const refreshMs = getRefreshFrequency(report.refreshFrequency);
    if (!refreshMs) {
      return;
    }

    // Set up interval to refresh the report
    refreshIntervalRef.current = setInterval(() => {
      try {
        // Only refresh if still in fullscreen "layout" mode
        if (embeddedReport && isFullscreenRef.current) {
          // Only refresh if still in fullscreen mode
          // Try to refresh the report using Power BI embed API
          // The refresh method reloads the data in the report
          try {
            if (typeof embeddedReport.refresh === 'function') {
              embeddedReport.refresh();
            } else if (typeof embeddedReport.reload === 'function') {
              // Fallback to reload if refresh is not available
              embeddedReport.reload();
            }
          } catch (refreshError: any) {
            console.error('Error calling refresh/reload:', refreshError);
            // Clear interval if refresh fails
            if (refreshIntervalRef.current) {
              clearInterval(refreshIntervalRef.current);
              refreshIntervalRef.current = null;
            }
          }
        } 
      } catch (error: any) {
        console.error('Error refreshing report:', error);
        // If error occurs, clear the interval to prevent repeated errors
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = null;
        }
      }
    }, refreshMs);

    // Cleanup function
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [isFullscreen, embeddedReport, activeTab, tabReportInfo]);

  // No longer listening to browser fullscreen events, since fullscreen
  // is now implemented as a layout mode controlled by state/CSS.

  return (
    <div className="report-viewer">
      <div className="report-tabs">
        {openTabs.filter((tabId: any) => tabReportInfo[tabId]).map((id: any) => {
          const report = getReportById(id);
          const label = report ? report.displayname : `Report ${id}`;
          return (
            <button
              key={`${report.workspaceid}-${report.reportid}`}
              className={activeTab === id ? 'active' : ''}
              onClick={() => handleTabClick(id)}
            >
              {label}
              <span
                className="tab-close-button"
                onClick={e => {
                  e.stopPropagation();
                  handleCloseTab(id);
                }}
                title={t('reports.closeTab')}
              >
                ×
              </span>
            </button>
          );
        })}
      </div>
      <div className="report-embed">
        {openTabs.length === 0 && (
          <div className="report-embed-message report-embed-message-mobile-center">
            {t('reports.noReportSelected')}
          </div>
        )}
        {openTabs.map((tabId: any) => {
          const report = getReportById(tabId);
          if (!report) return null;
          if (loadingEmbed[tabId]) {
            return (
              <div
                key={tabId}
                className={`report-embed-message${activeTab === tabId ? ' active' : ''}`}
                style={{ display: activeTab === tabId ? 'flex' : 'none' }}
              >
                {t('reports.loadingReport')}
              </div>
            );
          }
          if (embedError[tabId]) {
            return (
              <div
                key={tabId}
                className={`report-embed-message error${activeTab === tabId ? ' active' : ''}`}
                style={{ display: activeTab === tabId ? 'flex' : 'none' }}
              >
                {embedError[tabId]}
              </div>
            );
          }
          const embed = embedInfo[tabId];
          if (!embed || !embed.token || !embed.url) {
            return (
              <div
                key={tabId}
                className={`report-embed-message${activeTab === tabId ? ' active' : ''}`}
                style={{ display: activeTab === tabId ? 'flex' : 'none' }}
              >
                {t('reports.embedTokenRequired')}
              </div>
            );
          }
          return (
            <div
              key={tabId}
              className={`report-embed-tab-content${activeTab === tabId ? ' active' : ''}`}
              style={{ display: activeTab === tabId ? 'block' : 'none' }}
            >
              <PowerBIEmbed
                embedConfig={{
                  type: 'report',
                  id: report.reportid,
                  embedUrl: embed.url,
                  accessToken: embed.token,
                  tokenType: models.TokenType.Embed,
                  settings: {
                    panes: { filters: { visible: true, expanded: false }, pageNavigation: { visible: true } },
                    localeSettings: localeSettings,
                    // layoutType: models.LayoutType.MobilePortrait
                  },
                }}
                cssClassName="powerbi-embed"
                getEmbeddedComponent={(report) => {
                  setEmbeddedReport(report);
                  // Set embed as ready when component is available
                  if (report) {
                    // Wait for the embed to be fully loaded
                    try {
                      report.on('loaded', () => {
                        setEmbedReady(true);
                      });
                      // Also check if already loaded
                      if (report.iframe && report.iframe.contentWindow) {
                        setEmbedReady(true);
                      }
                    } catch (error) {
                      // If event listener fails, set ready after a short delay
                      setTimeout(() => setEmbedReady(true), 500);
                    }
                  }
                }}
                eventHandlers={
                  new Map([
                    [
                      'loaded',
                      () => {
                        setEmbedReady(true);
                      },
                    ],
                    [
                      'error',
                      (event: any) => {
                        // Handle token expiration errors
                        const errorDetail = event?.detail;
                        if (errorDetail) {
                          const errorCode = errorDetail.errorCode || errorDetail.code || '';
                          const errorMessage = errorDetail.message || '';
                          
                          // Check if it's a token expiration error
                          if (
                            errorCode === 'ClientError_TokenExpired' ||
                            errorMessage.includes('TokenExpired') ||
                            errorMessage.includes('token expired') ||
                            errorMessage.includes('expired token')
                          ) {
                            console.warn('Embed token expired, refreshing...', errorDetail);
                            if (tabId) {
                              refreshEmbedToken(tabId);
                            }
                          } else {
                            // Handle other errors
                            console.error('Power BI embed error:', errorDetail);
                            const errorMsg = errorMessage || errorCode || t('reports.errorLoadingReport');
                            dispatch({ 
                              type: 'tabs/setEmbedError', 
                              payload: { ...embedErrorRef.current, [tabId]: errorMsg } 
                            });
                          }
                        }
                      },
                    ],
                  ])
                }
              />
            </div>
          );
        })}
      </div>
      {/* Bottom bar for actions */}
      <div className="report-viewer-bottom-bar fab-mobile">
        <button
          className="report-viewer-bottom-bar-btn close-all"
          onClick={() => {
            dispatch({ type: 'tabs/removeAllTabs' });
            setSearchParams(new URLSearchParams());
          }}
          aria-label={t('reports.closeAllTabs')}
        >
          {FiXCircle({ size: 20 })}
          <span className="report-viewer-tooltip">{t('reports.closeAllTabs')}</span>
        </button>
        <button
          className="report-viewer-bottom-bar-btn share"
          onClick={handleShare}
          aria-label={t('reports.shareReport')}
          disabled={!activeTab || !tabReportInfo[activeTab]}
        >
          {FiShare2({ size: 20 })}
          <span className="report-viewer-tooltip">{t('reports.shareReport')}</span>
        </button>
        <button
          className="report-viewer-bottom-bar-btn fullscreen"
          onClick={handleFullscreen}
          aria-label={isFullscreen ? t('reports.exitFullscreen') : t('reports.fullscreen')}
        >
          {isFullscreen ? FiMinimize({ size: 20 }) : FiMaximize({ size: 20 })}
          <span className="report-viewer-tooltip">{isFullscreen ? t('reports.exitFullscreen') : t('reports.fullscreen')}</span>
        </button>
      </div>
      {/* Share Modal */}
      {showShareModal && (
        <div className="share-modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="share-modal" onClick={(e) => e.stopPropagation()}>
            <div className="share-modal-content">
              <div className="share-modal-icon">✓</div>
              <h3 className="share-modal-title">{t('reports.linkCopied')}</h3>
              <p className="share-modal-message">{t('reports.linkCopiedMessage')}</p>
              <button
                className="share-modal-button"
                onClick={() => setShowShareModal(false)}
              >
                {t('common.ok')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportViewer; 